import { Button } from "@shared/ui/button";
import { Card } from "@shared/ui/card";
import {
  MessageSquare,
  CreditCard,
  Wrench,
  AlertCircle,
  Pencil,
  ArrowRightLeft,
  PowerOff,
  Zap,
} from "lucide-react";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { CommunicationDialog } from "@features/cx/shared/components/CommunicationDialog";
import {
  useChannelTemplates,
  useChannelType,
  useSendCommunication,
} from "@features/cx/consumerManagement/hooks";
import { logEvent } from "@shared/analytics/analytics";
import { Consumer } from "@features/cx/consumerManagement/types";

interface QuickActionsProps {
  consumer: Consumer;
  className?: string;
  onUpdateDetails?: () => void;
}

export const QuickActions = ({
  consumer,
  className,
  onUpdateDetails,
}: QuickActionsProps) => {
  const navigate = useNavigate();
  const remoteUtilityId = useRemoteUtilityId();
  const [openSendComm, setOpenSendComm] = useState(false);

  const handleAction = (actionId: string) => {
    switch (actionId) {
      case "communicate":
        logEvent("Quick Action Used - Communicate");
        setOpenSendComm(true);
        break;
      case "payments":
        logEvent("Quick Action Used - Add Payments");
        navigate(
          `/cx/payments/create?consumerId=${consumer?.id}&search=${consumer?.consumerNo}`,
          {
            state: {
              prefilledConsumer: {
                consumerId: consumer?.id,
                consumerNo: consumer?.consumerNo,
                accountNumber: consumer?.consumerNo, // fallback for components expecting accountNumber
                name: `${consumer?.firstName} ${consumer?.lastName}`,
                phone: consumer?.contactNumber,
                email: consumer?.email,
                customerType: consumer?.categoryDisplay,
              },
            },
          },
        );
        break;
      case "service":
        logEvent("Quick Action Used - Request Service");
        navigate(
          `/cx/services/create?consumerId=${consumer?.id}&search=${consumer?.consumerNo}&source=account-management`,
          {
            state: {
              prefilledConsumer: {
                consumerId: consumer?.id,
                consumerNo: consumer?.consumerNo,
                accountNumber: consumer?.consumerNo, // fallback for components expecting accountNumber
                name: `${consumer?.firstName} ${consumer?.lastName}`,
                phone: consumer?.contactNumber,
                email: consumer?.email,
                customerType: consumer?.categoryDisplay,
              },
              source: "account-management",
            },
          },
        );
        break;
      case "complaint":
        logEvent("Quick Action Used - Complaint");
        navigate(
          `/cx/complaints/create?consumerId=${consumer?.id}&search=${consumer?.consumerNo}&source=account-management`,
          {
            state: {
              prefilledConsumer: {
                consumerId: consumer?.id,
                consumerNo: consumer?.consumerNo,
                name: `${consumer?.firstName} ${consumer?.lastName}`,
                phone: consumer?.contactNumber,
                email: consumer?.email,
                customerType: consumer?.categoryDisplay,
              },
              source: "account-management",
            },
          },
        );
        break;
      case "update":
        logEvent("Quick Action Used - Update Details");
        onUpdateDetails && onUpdateDetails();
        break;
      case "transfer":
        logEvent("Quick Action Used - Raise Transfer");
        navigate(
          `/cx/transfer/create?consumerId=${consumer?.id}&search=${consumer?.consumerNo}&source=account-management`,
          {
            state: {
              prefilledConsumer: {
                consumerId: consumer?.id,
                consumerNo: consumer?.consumerNo,
                accountNumber: consumer?.consumerNo, // fallback
                name: `${consumer?.firstName} ${consumer?.lastName}`,
                phone: consumer?.contactNumber,
                email: consumer?.email,
                customerType: consumer?.categoryDisplay,
              },
              source: "account-management",
            },
          },
        );
        break;
      case "disconnection":
        logEvent("Quick Action Used - Raise Disconnection");
        navigate(
          `/cx/disconnections/create?consumerId=${consumer?.id}&search=${consumer?.consumerNo}&source=account-management`,
          {
            state: {
              prefilledConsumer: {
                consumerId: consumer?.id,
                consumerNo: consumer?.consumerNo,
                accountNumber: consumer?.consumerNo, // fallback
                name: `${consumer?.firstName} ${consumer?.lastName}`,
                phone: consumer?.contactNumber,
                email: consumer?.email,
                customerType: consumer?.categoryDisplay,
              },
              source: "account-management",
            },
          },
        );
        break;
    }
  };

  const quickActions = [
    {
      id: "communicate",
      label: "Send Communication",
      icon: MessageSquare,
      variant: "secondary" as const,
      className:
        "w-full justify-start gap-3 bg-blue-100 text-blue-900 hover:bg-blue-200 border-none h-10",
    },
    {
      id: "payments",
      label: "Add Payment",
      icon: CreditCard,
      variant: "outline" as const,
      className:
        "w-full justify-start gap-3 h-10 text-gray-700 border-gray-200",
    },
    {
      id: "service",
      label: "Request Service",
      icon: Wrench,
      variant: "outline" as const,
      className:
        "w-full justify-start gap-3 h-10 text-gray-700 border-gray-200",
    },
    {
      id: "complaint",
      label: "Register Complaint",
      icon: AlertCircle,
      variant: "outline" as const,
      className:
        "w-full justify-start gap-3 h-10 text-gray-700 border-gray-200",
    },
    {
      id: "update",
      label: "Update Profile",
      icon: Pencil,
      variant: "outline" as const,
      className:
        "w-full justify-start gap-3 h-10 text-gray-700 border-gray-200",
    },
    {
      id: "transfer",
      label: "Raise Transfer Request",
      icon: ArrowRightLeft,
      variant: "outline" as const,
      className:
        "w-full justify-start gap-3 h-10 text-gray-700 border-gray-200 pt-4 border-t border-gray-100 mt-4 rounded-none",
    },
    {
      id: "disconnection",
      label: "Raise Disconnection",
      icon: PowerOff,
      variant: "outline" as const,
      className:
        "w-full justify-start gap-3 h-10 text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700 hover:border-red-300",
    },
  ];

  return (
    <Card className={`p-4 h-fit ${className}`}>
      <div className="flex items-center gap-2 mb-6">
        <Zap className="h-5 w-5" />
        <h3 className="font-semibold text-lg">Quick Actions</h3>
      </div>

      <div className="space-y-4">
        {quickActions.map((action) => {
          const ButtonComponent = (
            <Button
              key={action.id}
              className={action.className}
              variant={action.variant}
              onClick={() => handleAction(action.id)}
            >
              <action.icon className="h-5 w-5" />
              <span className="font-medium">{action.label}</span>
            </Button>
          );
          return ButtonComponent;
        })}
      </div>

      <CommunicationDialog
        isOpen={openSendComm}
        onClose={() => setOpenSendComm(false)}
        consumerId={consumer?.id.toString()}
        remoteUtilityId={Number(remoteUtilityId)}
        email={consumer?.email}
        phone={consumer?.contactNumber}
        useChannelType={useChannelType}
        useChannelTemplates={useChannelTemplates}
        useSendCommunication={useSendCommunication}
        communicationType="consumerDetailCommunication"
      />
    </Card>
  );
};
